import { useCallback, useState } from 'react';

export interface ImageDecodeFallback {
  /** The URL to render: the fallback once the primary fails and one exists, else the primary. */
  readonly effectiveSrc: string | undefined;
  /** True once the fallback image is the one being shown. */
  readonly showingFallback: boolean;
  /** True once the primary decode has failed; gates a fallback that is fetched only on demand. */
  readonly srcFailed: boolean;
  /**
   * False when nothing can paint yet — show a neutral stand-in. That is either the primary
   * having failed with no fallback available, or the fallback itself having failed to decode.
   */
  readonly canPaint: boolean;
  /** `<img onError>` handler: records a decode failure, of the fallback once it is the one shown. */
  readonly onError: () => void;
  /**
   * `<img onLoad>` check: records a zero-natural-size "load" as a decode failure and reports
   * whether it was one, so a caller with further load logic can skip it. Records against the
   * fallback once it is the one shown.
   */
  readonly onLoadCheck: (image: HTMLImageElement) => boolean;
}

/**
 * Sticky per-``src`` image decode-failure tracking with an optional fallback.
 *
 * Browsers disagree on how they signal an undecodable image: some fire ``error``,
 * others "load" it at zero natural size (a local ``blob:`` HEIC in Chrome/Edge).
 * Both count as a failure. The flag is sticky per ``src`` so a ``fallbackSrc``
 * resolved *after* the failure still swaps in, since the unchanged ``<img>`` will
 * not re-fire ``error``.
 */
export function useImageDecodeFallback(src: string | undefined, fallbackSrc?: string): ImageDecodeFallback {
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const [fallbackFailedSrc, setFallbackFailedSrc] = useState<string | undefined>(undefined);
  const srcFailed = failedSrc !== undefined && failedSrc === src;

  const showingFallback = srcFailed && fallbackSrc !== undefined;
  const effectiveSrc = showingFallback ? fallbackSrc : src;
  // The fallback is sticky per `fallbackSrc`, so a stale failure from a prior
  // fallback does not suppress a fresh one.
  const fallbackFailed = fallbackFailedSrc !== undefined && fallbackFailedSrc === fallbackSrc;
  const canPaint = !srcFailed || (showingFallback && !fallbackFailed);

  const onError = useCallback(() => {
    if (showingFallback) {
      setFallbackFailedSrc(fallbackSrc);
    } else {
      setFailedSrc(src);
    }
  }, [showingFallback, fallbackSrc, src]);

  const onLoadCheck = useCallback(
    (image: HTMLImageElement) => {
      if (image.naturalWidth !== 0) {
        return false;
      }
      if (showingFallback) {
        setFallbackFailedSrc(fallbackSrc);
      } else {
        setFailedSrc(src);
      }
      return true;
    },
    [showingFallback, fallbackSrc, src]
  );

  return { effectiveSrc, showingFallback, srcFailed, canPaint, onError, onLoadCheck };
}
